import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import { useI18nFormatters } from 'hooks/i18nFormatters';

import './style.less';

const I18N_BASE_PATH =
  'pages.private.KYBCompany.pages.companyDetails.subPages.summary.cards.financialRisk';

const KeyFinancialsTable = ({ keyFinancialsData }) => {
  const { t: translate } = useTranslation();
  const { i18nFormatCurrency } = useI18nFormatters();

  const data = useMemo(() => {
    const allYears = new Set();
    const turnover = [];
    const preTaxProfit = [];
    const shareholdersFunds = [];
    const numberOfEmployees = [];

    // TurnOver
    keyFinancialsData.turnover.forEach((s) => {
      const year = new Date(s.yearEndDate).getFullYear();
      if (!turnover.some((t) => t.originalYear === s.yearEndDate)) {
        allYears.add(year);
        turnover.push({
          year,
          originalYear: s.yearEndDate,
          value: i18nFormatCurrency(s.profitAndLoss.turnover, null, s.currency, {
            maximumFractionDigits: 0,
          }),
        });
      }
    });

    // Shareholder's Funds
    keyFinancialsData.shareholdersFunds.forEach((s) => {
      const year = new Date(s.yearEndDate).getFullYear();
      if (!shareholdersFunds.some((t) => t.originalYear === s.yearEndDate)) {
        allYears.add(year);
        shareholdersFunds.push({
          year,
          originalYear: s.yearEndDate,
          value: i18nFormatCurrency(
            s.balanceSheet.totalShareholdersEquity,
            null,
            s.currency,
            {
              maximumFractionDigits: 0,
            }
          ),
        });
      }
    });

    // Pre Tax Profit
    keyFinancialsData.preTaxProfit.forEach((s) => {
      const year = new Date(s.yearEndDate).getFullYear();
      if (!preTaxProfit.some((t) => t.originalYear === s.yearEndDate)) {
        allYears.add(year);
        preTaxProfit.push({
          year,
          originalYear: s.yearEndDate,
          value: i18nFormatCurrency(s.profitAndLoss.preTaxProfit, null, s.currency, {
            maximumFractionDigits: 0,
          }),
        });
      }
    });

    // Number of Employees
    keyFinancialsData.numberOfEmployees.forEach((e) => {
      if (!numberOfEmployees.some((t) => t.year === e.year)) {
        allYears.add(e.year);
        numberOfEmployees.push({
          year: e.year,
          value: e.numberOfEmployees,
        });
      }
    });

    const columns = [{ title: '', dataIndex: 'title' }];
    const rows = [
      { title: translate(`${I18N_BASE_PATH}.table.rows.turnOver`, 'TurnOver') },
      { title: translate(`${I18N_BASE_PATH}.table.rows.preTaxProfit`, 'Pre Tax Profit') },
      {
        title: translate(
          `${I18N_BASE_PATH}.table.rows.shareholdersFunds`,
          'Shareholders Funds'
        ),
      },
      {
        title: translate(
          `${I18N_BASE_PATH}.table.rows.numberOfEmployees`,
          'Number of Employees'
        ),
      },
    ];

    allYears.forEach((year) => {
      columns.push({
        title: year,
        dataIndex: year,
      });

      rows[0][year] = turnover.find((t) => t.year === year)?.value ?? '-';
      rows[1][year] = preTaxProfit.find((t) => t.year === year)?.value ?? '-';
      rows[2][year] = shareholdersFunds.find((t) => t.year === year)?.value ?? '-';
      rows[3][year] = numberOfEmployees.find((t) => t.year === year)?.value ?? '-';
    });

    return {
      columns,
      rows,
    };
  }, [keyFinancialsData]);

  return (
    <div className="KYB-key-financials-table">
      <b>{translate(`${I18N_BASE_PATH}.table.title`, 'Key financials')}</b>
      {data.columns.length > 1 ? (
        <Table
          columns={data.columns}
          dataSource={data.rows}
          pagination={false}
          size="small"
        />
      ) : (
        <div className="no-content">
          {translate(
            `${I18N_BASE_PATH}.table.noInformation`,
            'No key financials information to display'
          )}
        </div>
      )}
    </div>
  );
};

export default KeyFinancialsTable;
